import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import { MapPin, Edit, Trash2, Eye, Plus, Search, RefreshCw, UserPlus, X, Calendar, Clock } from "lucide-react";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

// ─── Allocation Modal ────────────────────────────────────────────────────────
const AllocationModal = ({ route, onClose, onSave }) => {
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuard, setSelectedGuard] = useState("");
  const [shiftLabel, setShiftLabel] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGuards();
  }, []);

  const fetchGuards = async () => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await fetch(api("/api/sentinel_users"), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch guards");
      }

      const data = await res.json();
      setGuards(data?.data || data || []);
    } catch (error) {
      console.error("Fetch guards error:", error);
      toast.error("Failed to load guards");
    } finally {
      setLoading(false);
    }
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedGuard) {
      toast.error("Please select a guard");
      return;
    }

    if (!startTime || !endTime) {
      toast.error("Please select start and end time");
      return;
    }

    if (new Date(startTime) >= new Date(endTime)) {
      toast.error("Start time must be before end time");
      return;
    }

    try {
      setSaving(true);
      const token = getToken();

      const payload = {
        route_id: route.id || route.route_id,
        guard_member_id: selectedGuard,
        shift_label: shiftLabel || "Regular Shift",
        start_time: new Date(startTime).toISOString(),
        end_time: new Date(endTime).toISOString(),
      };

      const res = await fetch(api("/api/placeAllocation"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to allocate route");
      }

      toast.success("Route allocated to guard successfully!");
      onSave();
      onClose();
    } catch (error) {
      console.error("Allocation error:", error);
      toast.error(error.message || "Failed to allocate route");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-2xl w-full max-w-[520px] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--divider)] flex items-center justify-between bg-[var(--surface)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[rgba(59,130,246,0.12)] flex items-center justify-center">
              <UserPlus size={16} className="text-[var(--accent)]" />
            </div>
            <div>
              <h3 className="text-[var(--text-primary)] text-[15px] font-extrabold">
                Allocate Route to Guard
              </h3>
              <p className="text-[var(--text-muted)] text-[11px]">
                {route?.route_name || "Route"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Guard Selection */}
          <div>
            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
              Select Guard <span className="text-[var(--red)]">*</span>
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-[var(--text-muted)] text-[13px] py-2">
                <div className="w-4 h-4 border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
                Loading guards...
              </div>
            ) : (
              <select
                value={selectedGuard}
                onChange={(e) => setSelectedGuard(e.target.value)}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] cursor-pointer"
                required
              >
                <option value="">— Select Guard —</option>
                {guards.map((guard) => (
                  <option
  key={guard.fld_memberId || guard.member_id || guard.id}
  value={guard.fld_memberId || guard.member_id || guard.id}
>
  {(guard.fld_name || guard.name || guard.guard_name || "Unknown")} (
  {guard.fld_memberId || guard.member_id || guard.id})
</option>
                ))}
              </select>
            )}
            {guards.length === 0 && !loading && (
              <p className="text-[11px] text-[var(--red)] mt-1">No guards available. Please add guards first.</p>
            )}
          </div>

          {/* Shift Label */}
          <div>
            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
              Shift Label
            </label>
            <input
              type="text"
              value={shiftLabel}
              onChange={(e) => setShiftLabel(e.target.value)}
              placeholder="e.g., Morning Shift, Night Shift"
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
              Start Time <span className="text-[var(--red)]">*</span>
            </label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              required
            />
          </div>

          {/* End Time */}
          <div>
            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
              End Time <span className="text-[var(--red)]">*</span>
            </label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold hover:bg-[var(--accent-faint)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="flex-1 border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: "var(--btn-bg)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving ? "Allocating..." : "Allocate Route"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function PlaceList() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [allocationModal, setAllocationModal] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchRoutes(token);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, routes]);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };
const handleViewAllocation = (route) => {
  const placeId = route.route_id || route.id;

  if (!placeId) {
    toast.error("Place ID not found");
    return;
  }

  navigate(`/place_allocation/${placeId}`);
};
  const fetchRoutes = async (token) => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await fetch(api("/api/routes"), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch routes");
      }

      const data = await res.json();
      const routeList = data?.data || data || [];
      setRoutes(routeList);
      setFilteredRoutes(routeList);
      toast.success(`Loaded ${routeList.length} routes`);
    } catch (error) {
      console.error("Fetch routes error:", error);
      toast.error(error.message || "Failed to load routes");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...routes];

    if (searchTerm.trim()) {
      const search = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (route) =>
          (route.route_name || "").toLowerCase().includes(search) ||
          (route.location || "").toLowerCase().includes(search) ||
          (route.route_id || route.id || "").toString().toLowerCase().includes(search)
      );
    }

    setFilteredRoutes(filtered);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    const token = getToken();
    fetchRoutes(token);
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm("Are you sure you want to delete this route?")) {
      return;
    }

    try {
      const token = getToken();
      const res = await fetch(api(`/api/routes/${routeId}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to delete route");
      }

      toast.success("Route deleted successfully!");
      handleRefresh();
    } catch (error) {
      console.error("Delete route error:", error);
      toast.error(error.message || "Failed to delete route");
    }
  };

  const handleEdit = (routeId) => {
    navigate(`/create_place?edit_id=${routeId}`);
  };

  const handleAllocate = (route) => {
    setAllocationModal(route);
  };

  const handleAllocationSave = () => {
    handleRefresh();
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  // Pagination
  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRoutes = filteredRoutes.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading routes...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="route-list"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      {/* Allocation Modal */}
      {allocationModal && (
        <AllocationModal
          route={allocationModal}
          onClose={() => setAllocationModal(null)}
          onSave={handleAllocationSave}
        />
      )}

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Route List
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Manage all routes and their locations
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRefresh}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate("/create_place")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 flex items-center gap-2"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              <Plus size={16} />
              Create Route
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Routes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {routes.length}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Active Routes
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {routes.filter((r) => r.is_active !== false).length}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Locations
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {new Set(routes.map((r) => r.location).filter(Boolean)).size}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="relative">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              placeholder="Search by route name, location, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-10 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-6 py-4 border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
                Routes Database
              </h3>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Complete list of all routes
              </p>
            </div>
            <span className="text-[13px] text-[var(--text-muted)]">
              {filteredRoutes.length} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            {currentRoutes.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)] text-[13px]">
                <MapPin size={40} className="opacity-20 mx-auto mb-3" />
                <p>No routes found</p>
                <button
                  type="button"
                  onClick={() => navigate("/create_place")}
                  className="mt-3 border-0 rounded-xl px-4 py-2 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                  style={{
                    background: "var(--btn-bg)",
                    color: "var(--btn-text)",
                    boxShadow: "0 4px 18px var(--accent-glow)",
                  }}
                >
                  Create your first route
                </button>
              </div>
            ) : (
              <table className="w-full min-w-[800px] text-[13px] font-['Inter',system-ui,sans-serif]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">#</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">Route Name</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">Location</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">Coordinates</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">Status</th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRoutes.map((route, index) => (
                    <tr
                      key={route.id || route.route_id || index}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        index % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <td className="px-4 py-3 text-[13px] text-[var(--text-muted)]">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)] font-extrabold">
                        {route.route_name || "-"}
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)]">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-[var(--accent)] flex-shrink-0" />
                          <span className="truncate max-w-[200px]">{route.location || "-"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-muted)] font-mono text-[12px]">
                        {route.latitude && route.longitude ? (
                          <span>
                            {parseFloat(route.latitude).toFixed(5)}, {parseFloat(route.longitude).toFixed(5)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
                            route.is_active !== false
                              ? "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
                              : "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
                          }`}
                        >
                          {route.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 flex-wrap">

 <button
  type="button"
  onClick={() => handleViewAllocation(route)}
  className="p-1.5 rounded-lg border border-[rgba(34,197,94,0.28)] bg-[rgba(34,197,94,0.12)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.2)] transition-colors"
  title="View Allocations"
>
  <Eye size={15} />
</button>

  <button
    type="button"
    onClick={() => handleAllocate(route)}
    className="p-1.5 rounded-lg border border-[rgba(59,130,246,0.28)] bg-[rgba(59,130,246,0.12)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.2)] transition-colors"
    title="Allocate to Guard"
  >
    <UserPlus size={15} />
  </button>

  <button
    type="button"
    onClick={() => handleEdit(route.id || route.route_id)}
    className="p-1.5 rounded-lg border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--accent-faint)] hover:text-[#f59e0b] transition-colors"
    title="Edit"
  >
    <Edit size={15} />
  </button>

  <button
    type="button"
    onClick={() => handleDelete(route.id || route.route_id)}
    className="p-1.5 rounded-lg border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--red-faint)] hover:text-[var(--red)] transition-colors"
    title="Delete"
  >
    <Trash2 size={15} />
  </button>

</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {filteredRoutes.length > 0 && (
            <div className="px-6 py-4 border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap bg-[var(--surface)]">
              <div className="text-[12px] text-[var(--text-muted)]">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredRoutes.length)} of{" "}
                {filteredRoutes.length} entries
              </div>

              <div className="flex gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
                >
                  <span className="sr-only">Previous</span> ←
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pg = i + 1;
                  if (totalPages > 5) {
                    if (currentPage > 3) {
                      pg = Math.min(currentPage - 2 + i, totalPages - 4 + i);
                      if (pg > totalPages) return null;
                    }
                  }
                  return (
                    <button
                      key={pg}
                      type="button"
                      onClick={() => goToPage(pg)}
                      className={`min-w-9 px-2.5 py-1.5 rounded-xl border font-mono text-[11px] font-extrabold transition-colors ${
                        currentPage === pg
                          ? "bg-[var(--btn-bg)] border-[var(--btn-bg)] text-[var(--btn-text)]"
                          : "bg-[var(--card-bg)] border-[var(--surface-border)] text-[var(--text-muted)] hover:bg-[var(--surface)]"
                      }`}
                    >
                      {pg}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
                >
                  → <span className="sr-only">Next</span>
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}